import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Tv, Settings, Server, Users, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import { useAuth } from '@contexts/AuthContext';
import { useHousehold } from '@contexts/HouseholdContext';
import type { FamilyMember } from '@lib/streamFlowApi';
import IPTVDeviceModal from './IPTVDeviceModal';

/**
 * Devices-first dashboard panel. Replaces the channel-grid first-paint
 * on Android TV / Shield where the user shouldn't be browsing channels
 * in our SPA at all — they install Tivimate / Smarters, point it at
 * the Xtream credentials we mint here, and use that as their viewer.
 *
 * Each row is one family member ("device"). Tap a row → IPTVDeviceModal
 * opens with the per-platform setup card (URL + username + password
 * + QR code + walkthrough). Add a new device with the big "+ New
 * Device" tile. Admin gets links out to Providers + Proxy Nodes +
 * Household Settings; non-admins just see their own devices.
 *
 * Doesn't render the channel grid, search bar, player, or favorites —
 * those are the native IPTV app's job.
 */
export default function DevicesPanel() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { activeHousehold } = useHousehold();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [iptvFor, setIptvFor] = useState<FamilyMember | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    const res = await streamFlowApi.listMembers();
    if (res.success) setMembers(res.data || []);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const handleNewDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreating(true);
    const res = await streamFlowApi.createMember({ display_name: trimmed, family_role: 'member' });
    setCreating(false);
    if (res.success && res.data) {
      toast.success(`Added "${trimmed}"`);
      setNewName('');
      await refresh();
      // Open the setup card immediately so the user can mint creds + scan QR
      setIptvFor(res.data);
    } else {
      toast.error(res.error?.message || 'Failed to add device');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl tv:text-3xl font-bold">Your Devices</h1>
          <p className="text-sm text-gray-500 mt-1">
            {activeHousehold?.name ?? 'Household'} —
            add a device per TV / phone / tablet, then paste the credentials into your IPTV app.
          </p>
        </div>
        <button
          onClick={() => signOut()}
          className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1.5 mt-1"
          title="Sign out"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </header>

      {/* Quick-add form */}
      <form onSubmit={handleNewDevice} className="bg-white border rounded-xl p-4 mb-6 flex items-center gap-3">
        <Plus className="w-5 h-5 text-slate-700 flex-shrink-0" />
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New device (e.g. Living Room Shield)"
          className="flex-1 px-3 py-2 border rounded-lg text-sm tv:text-base"
        />
        <button
          type="submit"
          disabled={creating || !newName.trim()}
          className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm tv:text-base disabled:opacity-50"
        >
          {creating ? 'Adding…' : 'Add device'}
        </button>
      </form>

      {/* Device tiles */}
      {loading ? (
        <p className="text-gray-500 text-center py-12">Loading devices…</p>
      ) : members.length === 0 ? (
        <div className="text-center py-12 text-gray-500 border border-dashed border-gray-300 rounded-xl">
          <Tv className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p>No devices yet. Add your first one above.</p>
          <p className="text-xs mt-1">Each device gets its own login for Tivimate / IPTV Smarters / OTT Navigator.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {members.map((m) => (
            <button
              key={m.id}
              onClick={() => setIptvFor(m)}
              className="text-left bg-white border rounded-xl p-4 hover:border-slate-500 hover:shadow-md transition-all focus-visible:ring-4 focus-visible:ring-blue-500 focus-visible:outline-none"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <Tv className="w-5 h-5 text-slate-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium tv:text-lg truncate">{m.display_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{m.family_role}</p>
                </div>
                <span className="text-xs text-blue-600 font-medium">Setup →</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Admin links — show even to non-admins (server gates the destinations) */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <button
          onClick={() => navigate('/members')}
          className="bg-white border rounded-lg px-3 py-3 text-xs tv:text-sm hover:bg-gray-50 flex flex-col items-center gap-1"
        >
          <Users className="w-4 h-4" /> Members
        </button>
        <button
          onClick={() => navigate('/providers')}
          className="bg-white border rounded-lg px-3 py-3 text-xs tv:text-sm hover:bg-gray-50 flex flex-col items-center gap-1"
        >
          <Settings className="w-4 h-4" /> Providers
        </button>
        <button
          onClick={() => navigate('/settings/proxy-nodes')}
          className="bg-white border rounded-lg px-3 py-3 text-xs tv:text-sm hover:bg-gray-50 flex flex-col items-center gap-1"
        >
          <Server className="w-4 h-4" /> Proxy nodes
        </button>
      </div>

      {iptvFor && (
        <IPTVDeviceModal
          member={iptvFor}
          onClose={() => { setIptvFor(null); refresh(); }}
        />
      )}
    </div>
  );
}
